import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Chip,
  Alert,
  CircularProgress,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  TextField,
  Menu,
  MenuItem,
  Grid,
  useTheme,
} from '@mui/material';
import {
  Highlight as HighlightIcon,
  Comment as CommentIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon,
  Save as SaveIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import AnnotationToolbar from './AnnotationToolbar';
import AnnotationLayer from './AnnotationLayer';
import CollaborativeView from './CollaborativeView';
import CompliancePanel from './CompliancePanel';
import RoutingPanel from './RoutingPanel';
import PDFOverlayViewer from './PDFOverlayViewerV2';
import { magicMarkerService } from '../magicMarkerService';
import { useTranslation } from '../../../store/languageStore';

interface DocumentAnnotatorProps {
  documentId: string;
  documentUrl?: string;
  documentContent?: any;
  enableCollaboration?: boolean;
  onAnnotationsChange?: (annotations: any[]) => void;
  onRoutingDecision?: (decision: any) => void;
}

interface Annotation {
  id: string;
  type: string;
  priority: string;
  field_name: string;
  message: string;
  coordinates: { x: number; y: number; width: number; height: number };
  page_number: number;
  created_by: string;
  created_at: string;
  resolved: boolean;
  replies: any[];
}

const DocumentAnnotator: React.FC<DocumentAnnotatorProps> = ({
  documentId,
  documentUrl,
  documentContent,
  enableCollaboration = false,
  onAnnotationsChange,
  onRoutingDecision,
}) => {
  const { t, tString } = useTranslation();
  const theme = useTheme();
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [showCompliance, setShowCompliance] = useState(false);
  const [showRouting, setShowRouting] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(false);
  const [complianceReport, setComplianceReport] = useState<any>(null);
  const [routingDecision, setRoutingDecision] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [collaborationSession, setCollaborationSession] = useState<any>(null);
  const [annotationMode, setAnnotationMode] = useState<string>('view');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);

  // Load annotations on mount
  useEffect(() => {
    if (documentId) {
      loadAnnotations();
    }
  }, [documentId]);

  const loadAnnotations = async () => {
    setIsLoading(true);
    try {
      const response = await magicMarkerService.annotateDocument({
        document_id: documentId,
        auto_detect: true,
        check_compliance: true,
        enable_routing: true,
      });

      if (response.annotations) {
        const flatAnnotations = flattenAnnotations(response.annotations);
        setAnnotations(flatAnnotations);
        onAnnotationsChange?.(flatAnnotations);
      }

      if (response.compliance_report) {
        setComplianceReport(response.compliance_report);
      }

      if (response.routing_decision) {
        setRoutingDecision(response.routing_decision);
        onRoutingDecision?.(response.routing_decision);
      }

      if (response.statistics) {
        setStatistics(response.statistics);
      }

      toast.success(tString('documentAnnotator.messages.success'));
    } catch (error) {
      console.error('Failed to load annotations:', error);
      toast.error(tString('documentAnnotator.messages.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const flattenAnnotations = (annotationGroups: any): Annotation[] => {
    const flat: Annotation[] = [];
    Object.keys(annotationGroups).forEach((group) => {
      if (Array.isArray(annotationGroups[group])) {
        annotationGroups[group].forEach((ann: any) => {
          flat.push({
            ...ann,
            group,
          });
        });
      }
    });
    return flat;
  };

  const handleAnnotationClick = (annotation: Annotation) => {
    setSelectedAnnotation(annotation);
    setAnchorEl(null);
  };

  const handleAddAnnotation = async (newAnnotation: any) => {
    try {
      if (enableCollaboration && collaborationSession) {
        const response = await magicMarkerService.addCollaborativeAnnotation(
          documentId,
          newAnnotation
        );
        if (response.annotation) {
          setAnnotations([...annotations, response.annotation]);
          toast.success(tString('documentAnnotator.messages.annotationAdded'));
        }
      } else {
        // Local annotation
        const localAnnotation = {
          ...newAnnotation,
          id: `local_${Date.now()}`,
          created_at: new Date().toISOString(),
          created_by: 'current_user',
          resolved: false,
          replies: [],
        };
        setAnnotations([...annotations, localAnnotation]);
      }
    } catch (error) {
      toast.error(tString('documentAnnotator.messages.addFailed'));
    }
  };

  const handleResolveAnnotation = async (annotationId: string, note?: string) => {
    try {
      if (enableCollaboration && collaborationSession) {
        await magicMarkerService.resolveAnnotation(documentId, {
          annotation_id: annotationId,
          resolution_note: note,
        });
      }

      setAnnotations(
        annotations.map((ann) =>
          ann.id === annotationId
            ? { ...ann, resolved: true, resolved_at: new Date().toISOString() }
            : ann
        )
      );
      toast.success(tString('documentAnnotator.messages.annotationResolved'));
    } catch (error) {
      toast.error(tString('documentAnnotator.messages.resolveFailed'));
    }
  };

  const handleStartCollaboration = async () => {
    if (!enableCollaboration) return;

    try {
      const session = await magicMarkerService.startCollaboration({
        document_id: documentId,
        enable_real_time: true,
      });
      setCollaborationSession(session);
      setShowCollaboration(true);
      toast.success(tString('documentAnnotator.messages.collaborationStarted'));
    } catch (error) {
      toast.error(tString('documentAnnotator.messages.collaborationFailed'));
    }
  };

  const handleExportAnnotations = async () => {
    try {
      const exported = await magicMarkerService.exportAnnotations({
        document_id: documentId,
        format: 'json',
        include_resolved: true,
        include_compliance: true,
      });

      // Download as file
      const blob = new Blob([JSON.stringify(exported, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `annotations_${documentId}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(tString('documentAnnotator.messages.annotationsExported'));
    } catch (error) {
      toast.error(tString('documentAnnotator.messages.exportFailed'));
    }
  };

  const getAnnotationIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'compliance':
        return <WarningIcon color="warning" />;
      case 'missing':
        return <VisibilityOffIcon color="action" />;
      case 'workflow':
        return <TimelineIcon color="primary" />;
      default:
        return <CommentIcon color="action" />;
    }
  };

  const getAnnotationColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
      {/* Main Document Area */}
      <Box sx={{ flex: 1, overflow: 'auto', position: 'relative' }}>
        {/* Toolbar */}
        <AnnotationToolbar
          annotationMode={annotationMode}
          onModeChange={setAnnotationMode}
          onRefresh={loadAnnotations}
          onExport={handleExportAnnotations}
          onToggleAnnotations={() => setShowAnnotations(!showAnnotations)}
          onToggleCompliance={() => setShowCompliance(!showCompliance)}
          onToggleRouting={() => setShowRouting(!showRouting)}
          onStartCollaboration={handleStartCollaboration}
          showAnnotations={showAnnotations}
          enableCollaboration={enableCollaboration}
        />

        {/* Statistics Bar */}
        {statistics && (
          <Box
            sx={{
              p: 1,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Chip
              label={tString('documentAnnotator.statistics.totalAnnotations', statistics.total_annotations)}
              size="small"
              icon={<CommentIcon />}
            />
            <Chip
              label={tString('documentAnnotator.statistics.criticalCount', statistics.critical_count)}
              size="small"
              color="error"
              icon={<ErrorIcon />}
            />
            <Chip
              label={tString('documentAnnotator.statistics.missingCount', statistics.missing_count)}
              size="small"
              color="warning"
              icon={<VisibilityOffIcon />}
            />
            <Chip
              label={tString('documentAnnotator.statistics.complete', Math.round(statistics.document_completeness))}
              size="small"
              color="success"
              icon={<CheckIcon />}
            />
            {complianceReport && !complianceReport.compliant && (
              <Alert severity="warning" sx={{ py: 0, flexGrow: 1 }}>
                {t('documentAnnotator.statistics.complianceIssues', complianceReport.critical_count)}
              </Alert>
            )}
          </Box>
        )}

        {/* Document Content */}
        <Box ref={documentRef} sx={{ position: 'relative', p: 2 }}>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Use PDFOverlayViewer for PDF documents with overlay */}
              {documentUrl ? (
                <PDFOverlayViewer
                  pdfUrl={documentUrl}
                  extractedData={documentContent}
                  annotations={annotations}
                  onFieldClick={(field, value) => {
                    // Handle field click for highlighting or editing
                    console.log('Field clicked:', field, value);
                  }}
                />
              ) : (
                <Paper sx={{ p: 3, minHeight: 600 }}>
                  {documentContent ? (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t('documentAnnotator.messages.pdfNotAvailable')}
                      </Alert>
                      <Grid container spacing={2}>
                        {Object.keys(documentContent).map((key) => {
                          const value = documentContent[key];
                          if (key === 'metadata' || key === 'ocr_confidence') return null;

                          return (
                            <Grid item xs={12} sm={6} key={key}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {key.replace(/_/g, ' ').toUpperCase()}
                                </Typography>
                                <Paper sx={{ p: 1, bgcolor: 'grey.50' }}>
                                  <Typography variant="body2">
                                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : value || '-'}
                                  </Typography>
                                </Paper>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {t('documentAnnotator.messages.noDocumentContent')}
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Annotation Layer - only show when not using PDFOverlayViewer */}
              {showAnnotations && !documentUrl && (
                <AnnotationLayer
                  annotations={annotations}
                  selectedAnnotation={selectedAnnotation}
                  onAnnotationClick={handleAnnotationClick}
                  onAddAnnotation={handleAddAnnotation}
                  annotationMode={annotationMode}
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Right Sidebar - Annotation List */}
      <Drawer
        anchor="right"
        variant="persistent"
        open={showAnnotations}
        sx={{
          width: 350,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 350,
            position: 'relative',
            height: '100%',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('documentAnnotator.title')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <List>
            {annotations.map((annotation) => (
              <ListItem
                key={annotation.id}
                button
                selected={selectedAnnotation?.id === annotation.id}
                onClick={() => handleAnnotationClick(annotation)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  border: 1,
                  borderColor: annotation.resolved ? theme.palette.success.main : 'divider',
                  opacity: annotation.resolved ? 0.6 : 1,
                }}
              >
                <ListItemIcon>{getAnnotationIcon(annotation.type)}</ListItemIcon>
                <ListItemText
                  primary={annotation.field_name}
                  secondary={
                    <>
                      <Typography variant="caption" component="div">
                        {annotation.message}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Chip
                          label={annotation.type}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={annotation.priority}
                          size="small"
                          color={getAnnotationColor(annotation.priority)}
                        />
                        {annotation.resolved && (
                          <Chip label={tString('documentAnnotator.status.resolved')} size="small" color="success" />
                        )}
                        {annotation.replies.length > 0 && (
                          <Badge badgeContent={annotation.replies.length} color="primary">
                            <CommentIcon fontSize="small" />
                          </Badge>
                        )}
                      </Box>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Compliance Panel */}
      {showCompliance && complianceReport && (
        <CompliancePanel
          report={complianceReport}
          onClose={() => setShowCompliance(false)}
        />
      )}

      {/* Routing Panel */}
      {showRouting && routingDecision && (
        <RoutingPanel
          decision={routingDecision}
          onClose={() => setShowRouting(false)}
        />
      )}

      {/* Collaboration View */}
      {showCollaboration && collaborationSession && (
        <CollaborativeView
          session={collaborationSession}
          documentId={documentId}
          onClose={() => setShowCollaboration(false)}
        />
      )}
    </Box>
  );
};

export default DocumentAnnotator;